class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.productsToShow = parseInt(this.dataset.productsToShow) || 8;
    this.showEmpty = this.dataset.showEmpty === 'true';
    this.storageKey = 'recently_viewed_products';
    this.currentProductHandle = this.getCurrentProductHandle();
  }

  connectedCallback() {
    this.saveCurrentProduct();
    this.loadRecentlyViewed();
  }

  getCurrentProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^\/\?]+)/);
    return match ? match[1] : null;
  }

  saveCurrentProduct() {
    if (!this.currentProductHandle) return;

    const recentlyViewed = this.getRecentlyViewed();
    const productData = {
      handle: this.currentProductHandle,
      timestamp: Date.now()
    };

    // Remove if already exists
    const existingIndex = recentlyViewed.findIndex(item => item.handle === this.currentProductHandle);
    if (existingIndex > -1) {
      recentlyViewed.splice(existingIndex, 1);
    }

    // Add to beginning
    recentlyViewed.unshift(productData);

    // Limit to max products
    if (recentlyViewed.length > this.productsToShow) {
      recentlyViewed.splice(this.productsToShow);
    }

    localStorage.setItem(this.storageKey, JSON.stringify(recentlyViewed));
  }

  getRecentlyViewed() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey)) || [];
    } catch (e) {
      return [];
    }
  }

  async loadRecentlyViewed() {
    const recentlyViewed = this.getRecentlyViewed();
    
    // Filter out current product
    const filteredProducts = recentlyViewed.filter(item => 
      item.handle !== this.currentProductHandle
    );

    if (filteredProducts.length === 0) {
      this.showEmptyState();
      return;
    }

    const productHandles = filteredProducts.slice(0, this.productsToShow).map(item => item.handle);
    
    try {
      const products = await this.fetchProducts(productHandles);
      if (products.length > 0) {
        this.renderProducts(products);
      } else {
        this.showEmptyState();
      }
    } catch (error) {
      console.error('Error loading recently viewed products:', error);
      this.showEmptyState();
    }
  }

  async fetchProducts(handles) {
    const productPromises = handles.map(handle => 
      fetch(`/products/${handle}.js`)
        .then(response => response.ok ? response.json() : null)
        .catch(() => null)
    );

    const products = await Promise.all(productPromises);
    return products.filter(product => product !== null);
  }

  async fetchAndRenderProducts(handles) {
    if (handles.length === 0) {
      this.showEmptyState();
      return;
    }

    // Fetch product data directly
    const productPromises = handles.map(handle => 
      fetch(`/products/${handle}.js`)
        .then(response => response.ok ? response.json() : null)
        .catch(() => null)
    );

    const products = await Promise.all(productPromises);
    const validProducts = products.filter(product => product !== null);
    
    if (validProducts.length > 0) {
      this.renderProducts(validProducts);
    } else {
      this.showEmptyState();
    }
  }

  renderProducts(products) {
    const container = this.querySelector('.recently-viewed-products');
    const emptyState = this.querySelector('.recently-viewed-empty');
    const grid = this.querySelector('.recently-viewed-grid');

    if (products.length === 0) {
      this.showEmptyState();
      return;
    }

    // Generate product cards HTML
    const productCards = products.map(product => this.createProductCard(product)).join('');
    grid.innerHTML = productCards;

    // Show the section
    container.style.display = 'block';
    if (emptyState) emptyState.style.display = 'none';

    // Initialize slider and other functionality
    this.initializeComponents();
  }

  createProductCard(product) {
    const productUrl = `/products/${product.handle}`;
    
    // Handle Shopify product.js structure
    let imageUrl = '';
    if (product.featured_image) {
      imageUrl = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
    } else if (product.images && product.images.length > 0) {
      const img = product.images[0];
      imageUrl = img.startsWith('//') ? `https:${img}` : img;
    }
    
    const showSlider = this.dataset.showSlider === 'true';
    const vendor = product.vendor || '';
    const title = product.title || 'Untitled Product';
    
    // Calculate pricing
    const price = product.price || 0;
    const compareAtPrice = product.compare_at_price || 0;
    const isOnSale = compareAtPrice > price;
    const percentOff = isOnSale ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100) : 0;
    
    // Calculate aspect ratio - using 125% like the example
    const ratioPercent = 125;
    
    return `
      <li class="grid__item ${showSlider ? ' slider__slide' : ''}" id="Slide-${this.sectionId}-${product.id}">
        <div class="card-wrapper wbproduct-container product-card-wrapper left">
          <div class="card card--card${imageUrl ? ' card--media' : ''} color-background-1 gradient" style="--ratio-percent: ${ratioPercent}%; background-color: #f5f5f5;">
            <div class="card__inner wbimgblock ratio" style="--ratio-percent: ${ratioPercent}%;">
              ${imageUrl ? `
                <div id="webipro-${this.sectionId}-${product.id}" class="card__media">
                  <div class="product__media-item" data-media-id="${this.sectionId}-${product.id}-${product.featured_media?.id || 'media'}">
                    <a href="${productUrl}" title="${this.escapeHtml(title)}" class="media media--transparent media-- media--hover-effect">
                      <img class="img-fluid mx-auto lazyload"
                           src="${imageUrl}" 
                           sizes="(min-width: 1800px) 417px, (min-width: 992px) calc((100vw - 130px) / 4), (min-width: 768px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)" 
                           alt="${this.escapeHtml(title)}" 
                           loading="eager" 
                           width="959" 
                           height="1200">
                    </a>
                  </div>
                </div>
              ` : ''}
              <div class="card__badge quick_add_position-top-left">
                ${isOnSale ? `
                  <span class="badge percent__badge-sale">
                    ${percentOff}% off
                  </span>
                ` : ''}
                <div class="badge soldout_product_badge color- sold-out-badge hidden">Sold out</div>
              </div>
            </div>
            <wb-card-variant>
              <div class="wbproductdes card__content">
                <product-info id="ProductInfo-${this.sectionId}-${product.id}" data-section-input="${this.sectionId}" data-section="${this.sectionId}-${product.id}" data-layout="card" data-url="${productUrl}">
                  <input type="hidden" name="id" class="product-variant-id" value="${product.variants?.[0]?.id || product.id}" disabled>
                  <div class="card__information">
                    <h3 class="cardhide_toggle card__heading h5" id="title-${this.sectionId}-${product.id}">
                      <a href="${productUrl}" id="CardLink-${this.sectionId}-${product.id}" class="full-unstyled-link" aria-labelledby="CardLink-${this.sectionId}-${product.id}">
                        ${this.escapeHtml(title)}
                      </a>
                    </h3>
                    <div class="card-information">
                      <span class="caption-large light cardhide_toggle"></span>
                      <div class="no-js-hidden wbhprice" id="price-${this.sectionId}-${product.id}" role="status">
                        <div class="card_product_Price">  
                          <span class="card_sale_price">
                            ${product.price_varies ? 'From ' : ''}
                            ${this.formatPrice(price)}
                          </span>
                          ${isOnSale ? `
                            <small class="card_compare_price">
                              ${this.formatPrice(compareAtPrice)}
                            </small>
                          ` : ''}
                        </div>
                        <div class="price price__container card__default_price${isOnSale ? ' price--on-sale show_badge' : ''}">
                          ${isOnSale ? `
                            <div class="price__regular"><span class="price-item price-item--regular">${this.formatPrice(price)}</span></div>
                            <div class="price__sale">
                              <span class="price-item price-item--sale price-item--last">
                                ${this.formatPrice(price)}
                              </span>
                              <small class="price-item price-item--regular">
                                ${this.formatPrice(compareAtPrice)}
                              </small>
                            </div>
                          ` : `
                            <div class="price__regular"><span class="price-item price-item--regular">${this.formatPrice(price)}</span></div>
                          `}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="recently-viewed-button-wrapper" style="margin: 1rem 0;">
                    <a href="${productUrl}" class="button button--primary button--full-width">
                      <span>View Product</span>
                    </a>
                  </div>
                </product-info>
              </div>
            </wb-card-variant>
          </div>
        </div>
      </li>
    `;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  formatPrice(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  showEmptyState() {
    if (!this.showEmpty) return;
    
    const container = this.querySelector('.recently-viewed-products');
    const emptyState = this.querySelector('.recently-viewed-empty');
    
    if (container) container.style.display = 'none';
    if (emptyState) emptyState.style.display = 'block';
  }

  initializeComponents() {
    // Initialize slider functionality
    this.initializeSlider();
    
    // Initialize other theme components
    this.initializeQuickAdd();
    this.initializeVariantPickers();
  }

  initializeSlider() {
    // Wait a bit for DOM to be ready
    setTimeout(() => {
      const sliderComponent = this.querySelector('slider-component');
      const prevButton = this.querySelector('.slider-button--prev');
      const nextButton = this.querySelector('.slider-button--next');
      const slider = this.querySelector('.slider');
      
      if (sliderComponent && prevButton && nextButton && slider) {
        // Remove existing event listeners
        const newPrevButton = prevButton.cloneNode(true);
        const newNextButton = nextButton.cloneNode(true);
        prevButton.parentNode.replaceChild(newPrevButton, prevButton);
        nextButton.parentNode.replaceChild(newNextButton, nextButton);
        
        this.setupSliderButtons(newPrevButton, newNextButton, slider);
      }
      
      // Try to initialize theme's slider component
      if (typeof window.SliderComponent !== 'undefined' && sliderComponent) {
        try {
          new window.SliderComponent(sliderComponent);
        } catch (e) {
          // Theme slider not available, using custom implementation
        }
      }
    }, 100);
  }

  setupSliderButtons(prevButton, nextButton, slider) {
    let currentIndex = 0;
    
    const getVisibleSlides = () => {
      const sliderRect = slider.getBoundingClientRect();
      const slideWidth = slider.querySelector('.slider__slide')?.offsetWidth || 0;
      return Math.floor(sliderRect.width / (slideWidth + 20)) || 1; // 20px gap
    };
    
    const getTotalSlides = () => {
      return slider.querySelectorAll('.slider__slide').length;
    };
    
    const updateSlider = () => {
      const slides = slider.querySelectorAll('.slider__slide');
      const slideWidth = slides[0]?.offsetWidth || 0;
      const gap = parseInt(getComputedStyle(slider).gap) || 20;
      const scrollPosition = currentIndex * (slideWidth + gap);
      
      slider.scrollTo({ 
        left: scrollPosition, 
        behavior: 'smooth' 
      });
      
      // Update button states
      const visibleSlides = getVisibleSlides();
      const totalSlides = getTotalSlides();
      
      prevButton.disabled = currentIndex === 0;
      nextButton.disabled = currentIndex >= totalSlides - visibleSlides;
      
      prevButton.style.opacity = currentIndex === 0 ? '0.5' : '1';
      nextButton.style.opacity = currentIndex >= totalSlides - visibleSlides ? '0.5' : '1';
    };
    
    prevButton.addEventListener('click', (e) => {
      e.preventDefault();
      if (currentIndex > 0) {
        currentIndex--;
        updateSlider();
      }
    });
    
    nextButton.addEventListener('click', (e) => {
      e.preventDefault();
      const visibleSlides = getVisibleSlides();
      const totalSlides = getTotalSlides();
      if (currentIndex < totalSlides - visibleSlides) {
        currentIndex++;
        updateSlider();
      }
    });
    
    // Initial update
    updateSlider();
    
    // Update on window resize
    window.addEventListener('resize', updateSlider);
  }

  initializeQuickAdd() {
    // Initialize quick add buttons if they exist
    const quickAddButtons = this.querySelectorAll('.quick-add__btn');
    quickAddButtons.forEach(button => {
      // Theme-specific quick add initialization would go here
      if (typeof window.QuickAdd !== 'undefined') {
        new window.QuickAdd(button);
      }
    });
  }

  initializeVariantPickers() {
    // Initialize variant pickers if they exist
    const variantPickers = this.querySelectorAll('variant-radios, variant-selects');
    variantPickers.forEach(picker => {
      // Theme-specific variant picker initialization would go here
      if (typeof window.VariantSelects !== 'undefined') {
        new window.VariantSelects(picker);
      }
    });
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);
